/**
 * 🟦 V12.7 — LyonAlcancePanel
 *
 * Modal "Lyon IA — Su Alcance dentro de DamyxIA" abierto desde el botón
 * `home-lyon-alcance` del HomepageModal. Solo lectura: muestra qué es Lyon
 * y qué puede hacer dentro de DamyxIA. No toca el Genio, ni el Builder,
 * ni lyonMemory: es informativo.
 *
 * 🟦 2026-06-27 — i18n bilingüe ES/EN via `useLanguage()`.
 */
import { createPortal } from 'react-dom';
import { useEffect } from 'react';
import { X, Sparkles, Zap, Link as LinkIcon, MoveDiagonal, ListChecks } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Section = ({ icon: Icon, title, children, testid }) => (
  <section
    data-testid={testid}
    className="mb-6 rounded-xl border border-purple-500/20 bg-slate-900/60 p-5"
  >
    <header className="flex items-center gap-2 mb-3">
      {Icon && <Icon className="w-4 h-4 text-purple-300" />}
      <h3 className="text-base font-semibold text-white">{title}</h3>
    </header>
    <div className="text-sm text-slate-300 space-y-2 leading-relaxed">
      {children}
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2">
    <span className="text-purple-300 mt-0.5">👉</span>
    <span>{children}</span>
  </li>
);

const LyonAlcancePanel = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const es = language === 'es';

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      className="fixed inset-0 z-[10001] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        data-testid="lyon-alcance-panel"
        className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-purple-500/30 shadow-2xl"
        style={{ background: '#0f0f1a', color: '#ffffff' }}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-purple-500/20"
          style={{
            background: 'linear-gradient(90deg, rgba(124,58,237,0.18), rgba(15,15,26,0.95))',
          }}
        >
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-5 h-5 text-purple-300" />
            <h2 className="text-base sm:text-lg font-bold tracking-tight text-white">
              {es ? 'Lyon IA — Su Alcance dentro de DamyxIA' : 'Lyon AI — Its Scope inside DamyxIA'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="lyon-alcance-close"
            aria-label={es ? 'Cerrar' : 'Close'}
            className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-purple-500/20 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div
          className="overflow-y-auto custom-scrollbar"
          style={{ maxHeight: 'calc(90vh - 64px)', padding: '24px' }}
        >
          <Section icon={Sparkles} title={es ? '¿Qué es Lyon IA?' : 'What is Lyon AI?'} testid="lyon-alcance-s1">
            <ul className="space-y-1.5 list-disc list-inside marker:text-purple-300">
              <li>{es ? 'Lyon es el asistente inteligente de DamyxIA.' : 'Lyon is the intelligent assistant of DamyxIA.'}</li>
              <li>{es ? 'Puede responder preguntas, guiarte paso a paso y ayudarte a usar el Builder.' : 'It can answer questions, guide you step by step and help you use the Builder.'}</li>
              <li>{es ? 'Conoce el contexto de tu proyecto y te explica cada sección.' : 'It knows the context of your project and explains each section.'}</li>
            </ul>
          </Section>

          <Section icon={Zap} title={es ? 'Capacidades principales' : 'Main capabilities'} testid="lyon-alcance-s2">
            <ul className="space-y-1.5">
              <Bullet>{es ? 'Puedes preguntarle cualquier cosa' : 'You can ask it anything'}</Bullet>
              <Bullet>{es ? 'Responde según el contexto real de DamyxIA' : 'Responds based on the real context of DamyxIA'}</Bullet>
              <Bullet>{es ? 'Te guía paso a paso' : 'Guides you step by step'}</Bullet>
              <Bullet>{es ? 'Explica qué hace cada sección del Builder' : 'Explains what each Builder section does'}</Bullet>
              <Bullet>{es ? 'Te dice cómo ejecutar acciones' : 'Tells you how to execute actions'}</Bullet>
              <Bullet>{es ? 'Te enseña a usar plantillas' : 'Teaches you how to use templates'}</Bullet>
              <Bullet>{es ? 'Te ayuda a crear dashboards' : 'Helps you create dashboards'}</Bullet>
              <Bullet>{es ? 'Te explica cómo usar Vision' : 'Explains how to use Vision'}</Bullet>
              <Bullet>{es ? 'Te explica cómo exportar a GitHub' : 'Explains how to export to GitHub'}</Bullet>
              <Bullet>{es ? 'Mantiene conversaciones multi-paso' : 'Maintains multi-step conversations'}</Bullet>
              <Bullet>{es ? 'Puede abrir paneles internos del Builder' : 'Can open internal Builder panels'}</Bullet>
              <Bullet>{es ? 'Puede aplicar Style Packs' : 'Can apply Style Packs'}</Bullet>
              <Bullet>{es ? 'Puede aplicar Recipes' : 'Can apply Recipes'}</Bullet>
              <Bullet>{es ? 'Puede inferir estilos desde imágenes' : 'Can infer styles from images'}</Bullet>
              <Bullet>{es ? 'Puede abrir el Marketplace' : 'Can open the Marketplace'}</Bullet>
              <Bullet>{es ? 'Puede abrir el Onboarding' : 'Can open the Onboarding'}</Bullet>
              <Bullet>{es ? 'Puede importar proyectos' : 'Can import projects'}</Bullet>
              <Bullet>{es ? 'Puede ayudarte a navegar el Builder' : 'Can help you navigate the Builder'}</Bullet>
            </ul>
          </Section>

          <Section icon={LinkIcon} title={es ? 'Integración con DamyxIA' : 'Integration with DamyxIA'} testid="lyon-alcance-s3">
            <ul className="space-y-1.5 list-disc list-inside marker:text-purple-300">
              <li>{es ? 'Lyon está conectado al Builder en tiempo real.' : 'Lyon is connected to the Builder in real time.'}</li>
              <li>{es ? 'No recarga el iframe.' : 'Does not reload the iframe.'}</li>
              <li>{es ? 'No rompe el árbol.' : 'Does not break the tree.'}</li>
              <li>{es ? 'Puede ejecutar acciones internas sin interrumpir tu trabajo.' : 'Can execute internal actions without interrupting your work.'}</li>
              <li>{es ? 'Puede abrir módulos como Style Packs, Marketplace, Onboarding y Recipes.' : 'Can open modules like Style Packs, Marketplace, Onboarding and Recipes.'}</li>
              <li>{es ? 'Puede explicar cualquier parte del sistema.' : 'Can explain any part of the system.'}</li>
            </ul>
          </Section>

          <Section icon={MoveDiagonal} title={es ? 'Ventana flotante del Genio' : 'Genie floating window'} testid="lyon-alcance-s4">
            <ul className="space-y-1.5 list-disc list-inside marker:text-purple-300">
              <li>{es ? 'Redimensionable por bordes (no por esquinas).' : 'Resizable by edges (not corners).'}</li>
              <li>{es ? 'Escalado proporcional del contenido.' : 'Proportional content scaling.'}</li>
              <li>{es ? 'Persistencia de tamaño y posición.' : 'Size and position persistence.'}</li>
              <li>{es ? 'Drag profesional desde el header.' : 'Professional drag from the header.'}</li>
              <li>{es ? 'Sin remount.' : 'No remount.'}</li>
              <li>{es ? 'Sin espacios vacíos.' : 'No empty spaces.'}</li>
              <li>{es ? 'Compatible con desktop y mobile.' : 'Compatible with desktop and mobile.'}</li>
            </ul>
          </Section>

          <Section icon={ListChecks} title={es ? 'Qué puedes hacer con Lyon IA' : 'What you can do with Lyon AI'} testid="lyon-alcance-s5">
            <ul className="space-y-1.5 list-disc list-inside marker:text-purple-300">
              <li>{es ? 'Aprender a usar DamyxIA desde cero.' : 'Learn how to use DamyxIA from scratch.'}</li>
              <li>{es ? 'Resolver dudas técnicas.' : 'Resolve technical doubts.'}</li>
              <li>{es ? 'Ejecutar acciones dentro del Builder.' : 'Execute actions inside the Builder.'}</li>
              <li>{es ? 'Aplicar estilos y recetas.' : 'Apply styles and recipes.'}</li>
              <li>{es ? 'Crear dashboards.' : 'Create dashboards.'}</li>
              <li>{es ? 'Usar Vision.' : 'Use Vision.'}</li>
              <li>{es ? 'Importar proyectos.' : 'Import projects.'}</li>
              <li>{es ? 'Navegar el Marketplace.' : 'Browse the Marketplace.'}</li>
              <li>{es ? 'Recibir explicaciones detalladas.' : 'Receive detailed explanations.'}</li>
              <li>{es ? 'Mantener conversaciones largas y guiadas.' : 'Maintain long, guided conversations.'}</li>
            </ul>
          </Section>

          <section
            data-testid="lyon-alcance-s6"
            className="mb-2 rounded-xl border border-purple-500/40 p-5"
            style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.18), rgba(34,211,238,0.08))' }}
          >
            <p className="text-sm text-purple-100 italic leading-relaxed">
              {es
                ? <>“Lyon IA es tu copiloto dentro de DamyxIA.<br />Está diseñado para ayudarte a construir, aprender y avanzar más rápido.”</>
                : <>“Lyon AI is your copilot inside DamyxIA.<br />It is designed to help you build, learn and move faster.”</>}
            </p>
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LyonAlcancePanel;
